import "./BsDropdown.scss";

import classNames from "classnames";
import { FormInput } from "components/Forms/FormInput";
import { delayOnOutsideClick } from "helpers";
import { createLogger } from "logging";
import React, { ReactNode, useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";

const logger = createLogger("BsDropdown");

export interface BsDropdownItem {
  name: string;
  title?: string;
  disabled?: boolean;
}

interface BsDropdownProps extends FormInput {
  items: (BsDropdownItem | null)[];
  displayValue: ReactNode;
  menuClass?: string;
  buttonClass?: string;
  itemClass?: string;
  renderItem?: (item: BsDropdownItem) => ReactNode;
  onClick: (item: BsDropdownItem) => void;
}

export const BsDropdown: React.FC<BsDropdownProps> = ({
  disabled,
  displayValue,
  items,
  onClick,
  buttonClass,
  itemClass,
  menuClass,
  renderItem,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonClassToUse = classNames("dropdown-toggle btn btn-secondary", buttonClass);
  const menuClassToUse = classNames("dropdown-menu dropdown-menu-right", menuClass, { show: isOpen });

  const outsideClickRef = useRef<HTMLDivElement>(null!);
  useOnClickOutside(outsideClickRef, () => {
    delayOnOutsideClick(logger, isOpen, setIsOpen);
  });

  return (
    <div className="btn-group" ref={outsideClickRef}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={isOpen ? "true" : "false"}
        className={buttonClassToUse}
        onClick={() => setIsOpen(!isOpen)}
        data-bs-toggle="dropdown"
      >
        {displayValue}
      </button>

      <ul tabIndex={-1} role="menu" aria-hidden={isOpen ? "false" : "true"} className={menuClassToUse}>
        {items.map((item, index) => {
          const key = `item-${index}`;

          if (!item) {
            return <li className="dropdown-divider" key={key} />;
          }

          const { disabled, title } = item;
          const itemClassToUse = classNames("dropdown-item", disabled ? "disabled" : "", itemClass);

          return (
            <li key={key}>
              <a
                onClick={(e) => {
                  e.preventDefault();
                  if (disabled) {
                    return;
                  }

                  onClick(item);
                  setIsOpen(false);
                }}
                className={itemClassToUse}
              >
                {renderItem ? renderItem(item) : title}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
